import { BlockedReason } from '../types/types.js';

/** Format a Date as YYYY-MM-DD in UTC. */
export const toUTCDateKey = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

export const calculateTokens = (pages: number, threads: number): number => {
  return pages + threads * 10;
};

/** Normalise a Date to 00:00:00 UTC on its day-of-month. */
const normaliseToMidnightUTC = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

/**
 * Build a period-anchor Date at midnight UTC, clamping the anchor day to the target month's
 * last valid day. Prevents `Date.UTC(year, month, 31)` from rolling over into the next month
 * (e.g. April has no 31st → would become May 1).
 */
const periodAnchor = (year: number, month: number, anchorDay: number): Date => {
  const lastDayOfMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(anchorDay, lastDayOfMonth)));
};

/**
 * Compute rolling monthly billing periods from an anchor date.
 *
 * Periods are normalised to 00:00:00 UTC based on the anchor day-of-month. This ensures that
 * a `billingStartDate` with a time component (e.g. 2026-04-12T12:00:00) still yields periods
 * starting at 00:00:00 on the anchor day (2026-04-12T00:00:00), avoiding an edge case where
 * the first half-day of usage falls outside the period boundary.
 *
 * Anchor days beyond a month's length are clamped to the last valid day (e.g. anchor day 31
 * in April → Apr 30). Period boundaries still abut: the end of period N equals the start of
 * period N+1 in every month.
 *
 * Returns `count` periods ending with the current in-progress one, oldest first.
 * E.g. anchor day 15, count 3, today 2026-04-20 → [{Feb 15–Mar 15}, {Mar 15–Apr 15}, {Apr 15–May 15}]
 */
export const computeBillingPeriods = (
  billingStartDate: Date,
  count: number,
): Array<{ start: Date; end: Date }> => {
  const anchorDay = billingStartDate.getUTCDate();
  const now = new Date();

  // Current period start: the most recent anchor day at midnight UTC that is on or before today
  let currentYear = now.getUTCFullYear();
  let currentMonth = now.getUTCMonth();
  if (periodAnchor(currentYear, currentMonth, anchorDay) > now) {
    currentMonth -= 1;
    if (currentMonth < 0) {
      currentMonth = 11;
      currentYear -= 1;
    }
  }

  const periods: Array<{ start: Date; end: Date }> = [];
  for (let i = count - 1; i >= 0; i--) {
    let startYear = currentYear;
    let startMonth = currentMonth - i;
    while (startMonth < 0) {
      startMonth += 12;
      startYear -= 1;
    }
    let endYear = startYear;
    let endMonth = startMonth + 1;
    if (endMonth > 11) {
      endMonth -= 12;
      endYear += 1;
    }
    periods.push({
      start: periodAnchor(startYear, startMonth, anchorDay),
      end: periodAnchor(endYear, endMonth, anchorDay),
    });
  }

  return periods;
};

/**
 * Compute the last `count` billing periods for a seat-based user, filtered to only periods
 * that should count under the user's current credit limit.
 *
 * Used by both the fair-usage streak check and the usage-chart view so they share the exact
 * same notion of "which periods count under the user's current limit".
 *
 * Eligibility rules (both must hold):
 *   - The period's `end` is after `creditLimitEffectiveFrom` — i.e. the credit limit was active
 *     during at least some of the period. This means the period CONTAINING the credit limit
 *     effective date is included (even if the limit kicked in mid-period) and so are all
 *     subsequent periods.
 *   - The period's `start` is on or after `billingStartDate` — i.e. the period is within the
 *     org's billing window.
 *
 * Example:
 *   billingStartDate         = 2026-03-12T12:00:00  (anchor day 12)
 *   creditLimitEffectiveFrom = 2026-04-16T13:00:00  (mid-period of Apr 12–May 12)
 *   Today is 2026-04-25  →  earliest eligible period starts 2026-04-12
 *   Rolling 3 periods back: [{Feb 12→Mar 12}, {Mar 12→Apr 12}, {Apr 12→May 12}]
 *   Filter keeps {Apr 12→May 12} only.
 *
 * Returns fewer than `count` periods if the org's billing or the user's current credit limit
 * has not been active long enough. Callers decide how to handle that:
 *   - Streak check: if the result has fewer than `count` periods, return not-exceeded.
 *   - Usage chart: display whatever is returned.
 */
export const getSeatBasedPeriods = (
  billingStartDate: Date,
  creditLimitEffectiveFrom: Date,
  count = 3,
): Array<{ start: Date; end: Date }> => {
  const normalisedBillingStart = normaliseToMidnightUTC(billingStartDate);
  const periods = computeBillingPeriods(billingStartDate, count);
  return periods.filter(
    (p) => p.end > creditLimitEffectiveFrom && p.start >= normalisedBillingStart,
  );
};

export interface BlockedReasonMessage {
  title: string;
  description: string;
}

/** Structured title + description for a blocked user when they try to perform a blocked action. */
export const blockedReasonMessage = (
  reason: BlockedReason | null,
  blockedUntil: Date | null,
): BlockedReasonMessage => {
  const until =
    blockedUntil !== null
      ? ` You are blocked until ${new Date(blockedUntil).toLocaleDateString('en-GB')}.`
      : '';
  const contact = ' Please contact your administrator or support.';
  switch (reason) {
    case BlockedReason.FairUsageStreak:
      return {
        title: 'Fair-usage limit exceeded',
        description: `You have exceeded your credit limit for 3 consecutive months.${until}${contact}`,
      };
    case BlockedReason.ShadowLimitExceeded:
      return {
        title: 'Hard usage limit exceeded',
        description: `You have exceeded the hard usage limit for this period.${until}${contact}`,
      };
    case BlockedReason.OrgLimitExceeded:
      return {
        title: 'Organisation over its credit limit',
        description: `Your organisation has exceeded its credit limit for this billing period.${until}${contact}`,
      };
    default:
      return {
        title: 'Account blocked',
        description: `You are currently blocked from approving runs.${until}${contact}`,
      };
  }
};

/** Backwards-compat flat string formatter (used where only a single-line message is needed, e.g. backend ForbiddenException). */
export const blockedReasonMessageFlat = (
  reason: BlockedReason | null,
  blockedUntil: Date | null,
): string => {
  const { title, description } = blockedReasonMessage(reason, blockedUntil);
  return `${title}. ${description}`;
};
